/* eslint-disable no-underscore-dangle */
import React, { useContext, useEffect, useMemo, useState } from 'react'
import { useParams } from 'react-router-dom'
import { useApolloClient, useMutation, useQuery } from '@apollo/client'
import {
  Box,
  Stack,
  Paper,
  Typography,
  TextField,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  OutlinedInput,
  Checkbox,
  ListItemText,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Alert,
} from '@mui/material'
import _get from 'lodash/get'

import AuthContext from '../../contexts/AuthContext'
import { useToast } from '../../contexts/ToastContext'
import Breadcrumb from '../../components/BreadcrumbLayout'
import { ActivityTableSection, ActivitySnapshotDialog } from '../../components/activities'
import activitiesPaginationQuery from '../../graphql/query/activitiesPagination'
import removeActivitiesMutation from '../../graphql/mutation/removeActivities'
import eventByIdQuery from '../../graphql/query/eventById'
import { invalidateActivityDerivedCaches } from '../../utils/cache'

const STATUS_OPTIONS = [
  'checkin',
  'manualcheckin',
  'selfcheckin',
  'pickup',
  'faceChecking',
  'faceMatched',
  'faceUnmatched',
]

const ActivitiesPage = () => {
  const { eventId } = useParams()
  const { user } = useContext(AuthContext)
  const { showSuccess, showError, showWarning } = useToast()
  const client = useApolloClient()

  const [statusFilter, setStatusFilter] = useState([])
  const [bibInput, setBibInput] = useState('')
  const [bibFilter, setBibFilter] = useState('')
  const [dateFrom, setDateFrom] = useState('')
  const [dateTo, setDateTo] = useState('')
  const [page, setPage] = useState(1)
  const [perPage, setPerPage] = useState(25)

  const [selectedIds, setSelectedIds] = useState([])
  const [pendingDeleteIds, setPendingDeleteIds] = useState([])
  const [reason, setReason] = useState('')
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false)
  const [deleting, setDeleting] = useState(false)
  const [rawRecord, setRawRecord] = useState(null)

  // Debounce the bib box so typing does not fire a request per keystroke.
  useEffect(() => {
    const timer = setTimeout(() => setBibFilter(bibInput.trim()), 400)
    return () => clearTimeout(timer)
  }, [bibInput])

  // Any filter change invalidates the current page number.
  useEffect(() => {
    setPage(1)
  }, [statusFilter, bibFilter, dateFrom, dateTo, perPage])

  const filter = useMemo(() => {
    const next = { eventId }
    if (statusFilter.length > 0) next.statusIn = statusFilter
    if (bibFilter) next.bibContains = bibFilter
    if (dateFrom) next.createdAtFrom = new Date(`${dateFrom}T00:00:00`).toISOString()
    if (dateTo) next.createdAtTo = new Date(`${dateTo}T23:59:59.999`).toISOString()
    return next
  }, [eventId, statusFilter, bibFilter, dateFrom, dateTo])

  const event = useQuery(eventByIdQuery, { variables: { _id: eventId } })
  const activities = useQuery(activitiesPaginationQuery, {
    variables: { filter, sort: 'CREATEDAT_DESC', page, perPage },
    fetchPolicy: 'cache-and-network',
  })
  const [removeActivities] = useMutation(removeActivitiesMutation)

  const items = _get(activities, 'data.activitiesPagination.items', [])
  const totalCount = _get(activities, 'data.activitiesPagination.count', 0)
  const pageCount = _get(activities, 'data.activitiesPagination.pageInfo.pageCount', 1)

  const handleToggleSelect = (id) => {
    setSelectedIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]))
  }

  const handleToggleSelectAllOnPage = (pageIds, select) => {
    setSelectedIds((prev) => {
      if (select) return [...new Set([...prev, ...pageIds])]
      return prev.filter((id) => !pageIds.includes(id))
    })
  }

  const openDeleteFor = (ids) => {
    setPendingDeleteIds(ids)
    setReason('')
    setOpenDeleteDialog(true)
  }

  const handleCloseDialog = () => {
    if (deleting) return
    setOpenDeleteDialog(false)
    setPendingDeleteIds([])
  }

  // Single-row and bulk delete share one path — the row trash icon just preloads one id.
  const handleConfirmDelete = async () => {
    setDeleting(true)
    try {
      const { data } = await removeActivities({
        variables: { _id: pendingDeleteIds, eventId, reason: reason.trim() || null },
      })
      const deletedCount = _get(data, 'removeActivities.deletedCount', 0)
      const notFoundIds = _get(data, 'removeActivities.notFoundIds', []) || []

      await activities.refetch()
      invalidateActivityDerivedCaches(client)

      setSelectedIds([])
      setPendingDeleteIds([])
      setOpenDeleteDialog(false)
      showSuccess(
        `Deleted ${deletedCount} activity record(s). A snapshot was saved to the deletion log.`,
        'Delete Successful'
      )
      if (notFoundIds.length > 0) {
        showWarning(`${notFoundIds.length} record(s) had already been removed.`)
      }
    } catch (error) {
      console.error('Error deleting activities:', error)
      const message =
        _get(error, 'graphQLErrors[0].message') ||
        error.message ||
        'Failed to delete activities. Please try again.'
      showError(message)
      setOpenDeleteDialog(false)
    } finally {
      setDeleting(false)
    }
  }

  // Early returns - MUST be after all hooks
  if (user.role !== 'Admin') {
    return 'Cannot Access'
  }

  const eventName = _get(event, 'data.eventId.name', '')
  const pendingIncludesPickup = items.some(
    (a) => pendingDeleteIds.includes(a._id) && a.status === 'pickup'
  )

  return (
    <Box>
      <Breadcrumb
        path="/events"
        titleLink="Events"
        path2={`/event/${eventId}/participant`}
        titleLink2={eventName || 'Event'}
        titleTypo="Activity"
      />

      <Box sx={{ mb: { xs: 2.5, sm: 3 } }}>
        <Typography
          variant="h4"
          component="h1"
          sx={{
            fontWeight: 700,
            fontSize: { xs: '1.5rem', sm: '1.75rem', md: '2rem' },
            mb: { xs: 0.5, sm: 1 },
          }}>
          Activity
        </Typography>
        <Typography
          variant="body2"
          sx={{
            color: 'text.secondary',
            fontSize: { xs: '0.8125rem', sm: '0.875rem', md: '0.9375rem' },
          }}>
          Every check-in and pickup recorded for this event. Deleting a record is permanent, but a
          full snapshot is kept in the deletion log.
        </Typography>
      </Box>

      <Paper
        elevation={0}
        sx={{
          border: 1,
          borderColor: 'divider',
          borderRadius: 2,
          p: { xs: 1.5, sm: 2 },
          mb: { xs: 2, sm: 2.5 },
        }}>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={{ xs: 1.5, md: 2 }}
          alignItems={{ xs: 'stretch', md: 'center' }}>
          <TextField
            size="small"
            label="Bib"
            value={bibInput}
            onChange={(e) => setBibInput(e.target.value)}
            sx={{ minWidth: { md: 160 } }}
          />
          <FormControl size="small" sx={{ minWidth: { md: 220 } }}>
            <InputLabel id="activity-status-filter-label">Status</InputLabel>
            <Select
              labelId="activity-status-filter-label"
              multiple
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              input={<OutlinedInput label="Status" />}
              renderValue={(selected) => selected.join(', ')}>
              {STATUS_OPTIONS.map((status) => (
                <MenuItem key={status} value={status}>
                  <Checkbox size="small" checked={statusFilter.includes(status)} />
                  <ListItemText primary={status} />
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            size="small"
            type="date"
            label="From"
            value={dateFrom}
            onChange={(e) => setDateFrom(e.target.value)}
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            size="small"
            type="date"
            label="To"
            value={dateTo}
            onChange={(e) => setDateTo(e.target.value)}
            InputLabelProps={{ shrink: true }}
          />
          <Button
            onClick={() => {
              setBibInput('')
              setStatusFilter([])
              setDateFrom('')
              setDateTo('')
            }}
            sx={{ textTransform: 'none', color: 'text.secondary' }}>
            Reset
          </Button>
        </Stack>
      </Paper>

      {activities.error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {activities.error.message}
        </Alert>
      )}

      <ActivityTableSection
        activities={items}
        totalCount={totalCount}
        loading={activities.loading}
        selectedIds={selectedIds}
        onToggleSelect={handleToggleSelect}
        onToggleSelectAllOnPage={handleToggleSelectAllOnPage}
        onClearSelection={() => setSelectedIds([])}
        onDeleteSelectedClick={() => openDeleteFor(selectedIds)}
        onDeleteRowClick={(row) => openDeleteFor([row._id])}
        onViewRawClick={(row) => setRawRecord(row)}
        page={page}
        perPage={perPage}
        pageCount={pageCount}
        onPageChange={setPage}
        onPerPageChange={setPerPage}
      />

      <Dialog open={openDeleteDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 600 }}>
          Delete {pendingDeleteIds.length} activity record
          {pendingDeleteIds.length !== 1 ? 's' : ''}?
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            This permanently removes the record{pendingDeleteIds.length !== 1 ? 's' : ''} and cannot
            be undone. A full snapshot and your name are saved to the deletion log first.
          </DialogContentText>
          <Alert severity="warning" sx={{ mb: 2 }}>
            Check-in counts and participant status will change accordingly.
            {pendingIncludesPickup && ' Deleting a pickup also increases the remaining stock.'}
          </Alert>
          <TextField
            fullWidth
            size="small"
            label="Reason (optional)"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="e.g. duplicate scan"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} disabled={deleting} sx={{ textTransform: 'none' }}>
            Cancel
          </Button>
          <Button
            onClick={handleConfirmDelete}
            variant="contained"
            color="error"
            disabled={deleting || pendingDeleteIds.length === 0}
            sx={{ textTransform: 'none' }}>
            {deleting ? 'Deleting…' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      <ActivitySnapshotDialog
        open={Boolean(rawRecord)}
        onClose={() => setRawRecord(null)}
        title="Activity record"
        data={rawRecord}
      />
    </Box>
  )
}

export default ActivitiesPage
